import { useCallback, useEffect, useState } from 'react'
import { Button, EmptyState, SearchInput } from '../../../shared/ui'
import {
  SUPPRESSION_PAGE,
  addSuppressions,
  listSuppressions,
  removeSuppression,
} from '../../../lib/outreach/api'
import { isValidSuppression, normalizeSuppressionValue } from '../../../lib/outreach/csv'
import type { Suppression, SuppressionKind } from '../../../lib/outreach/types'
import { toast } from '../../../lib/toast'
import { Pagination } from '../../shared/Pagination'
import { ConfirmButton, ErrorLine, errorMessage, reportError } from './shared'
import { SuppressionImport, resultSentence } from './SuppressionImport'

const KIND_LABEL: Record<SuppressionKind, string> = {
  email: 'Email address',
  domain: 'Domain',
  company: 'Company name',
}

interface Query {
  search: string
  page: number
}

export function SuppressionsTab() {
  const [query, setQuery] = useState<Query>({ search: '', page: 1 })
  const [rows, setRows] = useState<Suppression[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [kind, setKind] = useState<SuppressionKind>('email')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [addError, setAddError] = useState('')
  const [addMessage, setAddMessage] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const r = await listSuppressions({ search: query.search, page: query.page })
      setRows(r.rows)
      setTotal(r.total)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [query])

  useEffect(() => {
    void load()
  }, [load])

  const onSearch = useCallback((search: string) => {
    setQuery((q) => (q.search === search ? q : { search, page: 1 }))
  }, [])

  const add = async () => {
    setAddError('')
    setAddMessage('')
    if (!isValidSuppression(kind, value)) {
      setAddError(
        kind === 'email'
          ? 'Enter a full email address.'
          : kind === 'domain'
            ? 'Enter a domain such as example.com.'
            : 'Enter a company name.',
      )
      return
    }
    setBusy(true)
    try {
      const r = await addSuppressions([
        {
          kind,
          value: normalizeSuppressionValue(kind, value),
          source: 'manual',
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      ])
      setAddMessage(resultSentence(r))
      toast(resultSentence(r), 'success')
      setValue('')
      setReason('')
      await load()
    } catch (e) {
      setAddError(reportError(e))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (s: Suppression) => {
    try {
      await removeSuppression(s.id)
      toast('Removed from the list', 'success')
      await load()
    } catch (e) {
      setError(reportError(e))
    }
  }

  return (
    <div className="os-root">
      <p className="settings-hint">
        Email blocks one address. Domain blocks the domain and its subdomains. Company blocks a
        company name. The list applies to every program in this business, including follow-ups.
      </p>

      <div className="os-card">
        <div className="os-grid">
          <div className="settings-row os-field">
            <label className="settings-label" htmlFor="sup-kind">
              Kind
            </label>
            <select
              id="sup-kind"
              className="form-select"
              value={kind}
              onChange={(e) => setKind(e.target.value as SuppressionKind)}
            >
              {(Object.keys(KIND_LABEL) as SuppressionKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
          <div className="settings-row os-field">
            <label className="settings-label" htmlFor="sup-value">
              Value
            </label>
            <input
              id="sup-value"
              className="settings-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <div className="settings-row os-field">
            <label className="settings-label" htmlFor="sup-reason">
              Reason (optional)
            </label>
            <input
              id="sup-reason"
              className="settings-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <ErrorLine>{addError}</ErrorLine>
        {addMessage && <div className="os-ok">{addMessage}</div>}
        <div className="os-bar">
          <Button disabled={busy} onClick={() => void add()}>
            Add to the list
          </Button>
        </div>
      </div>

      <SuppressionImport
        onImported={() => {
          setQuery((q) => ({ ...q, page: 1 }))
          void load()
        }}
      />

      <div className="os-bar">
        <SearchInput value="" onChange={onSearch} placeholder="Search values" />
        <span className="settings-hint" aria-live="polite">
          {query.search
            ? `${total.toLocaleString()} matching`
            : `${total.toLocaleString()} on the list`}
        </span>
      </div>
      <ErrorLine>{error}</ErrorLine>

      {!loading && rows.length === 0 ? (
        <EmptyState
          title={query.search ? 'No matches' : 'The list is empty'}
          message={
            query.search
              ? 'Nothing on the list matches that search.'
              : 'Add an address, domain or company above, or import a CSV.'
          }
        />
      ) : (
        <table className="os-table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Value</th>
              <th>Reason</th>
              <th>Source</th>
              <th>Added</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id}>
                <td>{s.kind}</td>
                <td>{s.value}</td>
                <td>{s.reason ?? ''}</td>
                <td>{s.source ?? ''}</td>
                <td>{s.created_at.slice(0, 10)}</td>
                <td>
                  <ConfirmButton
                    variant="secondary"
                    label="Remove"
                    confirmLabel="Yes, remove"
                    onConfirm={() => void remove(s)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Pagination
        total={total}
        page={query.page}
        pageSize={SUPPRESSION_PAGE}
        onPageChange={(page) => setQuery((q) => ({ ...q, page }))}
      />
    </div>
  )
}
